import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { MetricColorScheme, VizOptions } from '@/types.ts';

/**
 * @fileoverview Viz Store for the legislative visualization application.
 *
 * The Viz Store manages visualization options and transient UI state:
 * - Contains visualization settings: colors, bin numbers, display options
 * - Manages transient UI state: hover states, tooltip positions, drawer visibility
 * - Provides actions to update visualization preferences
 * - **Partially persisted** - visualization options are saved to localStorage,
 *   but transient UI state (hover, tooltip, drawer) is not persisted
 *
 * Note: `drawerOpen` is included here as transient UI state, even though it's
 * not strictly a visualization setting, because the Viz Store already manages
 * other transient UI interactions (hover, tooltip).
 *
 * This separation allows the application to:
 * - Keep data loading logic separate from presentation logic
 * - Persist user preferences without persisting large data sets
 * - Clear data independently of visualization settings
 */

/**
 * Helper type to properly type Zustand hooks with equality function support.
 * This fixes TypeScript inference issues when using middleware with equality functions.
 */
type UseStoreWithEquality<T> = <U>(
  selector: (state: T) => U,
  equalityFn?: (a: U, b: U) => boolean,
) => U;

/**
 * Map tooltip position coordinates.
 *
 * These are viewport (screen) coordinates used to position the map tooltip
 * near the user's cursor for both transient hover and click-to-lock states.
 */
interface MapTooltipPos {
  x: number;
  y: number;
}

/**
 * Default visualization options used when initializing or resetting the viz store.
 */
const initialViz: VizOptions = {
  showPoints: true,
  binNum: 5,
  useBins: true,

  polyColor: 'metric',
  pointsColor: 'voteType',
  metricColorScheme: 'Greys',
  voteSupportColor: '#2e7d32',
  voteAgainstColor: '#cccc1b',
  voteAbsenceColor: '#EEEEEE',
  voteNoDataColor: '#CCCCCC',

  dataDrawerOpen: false,
  drawerOpen: false,
  useFeatureLabel: false,
};

/**
 * State interface for the Viz Store.
 *
 * The Viz Store manages visualization options and transient UI state including:
 * - Visualization settings (colors, bin numbers, display options)
 * - Transient UI state (hover states, tooltip positions, drawer visibility)
 *
 * **Persistence**:
 * - **Persisted to localStorage**: All visualization settings (colors, bins, display options)
 * - **NOT persisted** (resets on page reload): Transient UI state including:
 *   - `hoveredDistricts`, `hoverSource`, `selectedDistrictId`, `selectedDistricts`
 *   - `mapTooltipPos`
 *   - `drawerOpen`
 *
 * Note: `drawerOpen` is included here as transient UI state, even though it's
 * not strictly a visualization setting, because the Viz Store already manages
 * other transient UI interactions (hover, tooltip).
 *
 * @see VizOptions - Base visualization options type
 * @see useVizStore - Hook to access this store
 */
export type VizStore = VizOptions & {
  // Hover + selection state (not persisted)
  /**
   * Array of district IDs currently being hovered or selected.
   *
   * For map interactions, this will typically be a single ID, but for plot
   * interactions multiple districts can be highlighted at once.
   */
  hoveredDistricts: string[];
  /**
   * Source of the current hover/selection state.
   *
   * - `'map'`  → state driven by interactions on the map (hover or click-to-lock)
   * - `'plot'` → state driven by interactions on the scatter/plot
   * - `null`   → no active hover/selection
   */
  hoverSource: 'map' | 'plot' | null;
  /**
   * District that has been explicitly selected on the map via click.
   *
   * When non-null, this "locks" the hover state and tooltip to the selected
   * district until it is cleared (e.g., by clicking the map background or
   * clicking the same district again).
   */
  selectedDistrictId: string | null;
  /**
   * Districts that have been explicitly selected from plot click.
   *
   * When non-empty, this "locks" the hover state to these districts.
   * Used for multi-selection from plot interactions.
   */
  selectedDistricts: string[];
  /**
   * Current map tooltip position, if tooltip is visible.
   *
   * This is updated both on hover and on click-to-lock, and is cleared when
   * the hover/selection is cleared.
   */
  mapTooltipPos: MapTooltipPos | null;

  // Visualization settings actions
  /** Set the number of bins for metric color mapping */
  setBinNum: (n: number) => void;
  /** Toggle visibility of points on the plot */
  setShowPoints: (b: boolean) => void;
  /** Set whether to use discrete bins (true) or continuous gradient (false) for metric colors */
  setUseBins: (b: boolean) => void;

  // Color options
  /** Set polygon color mode ('voteType' or 'metric') */
  setPolyColor: (color: 'voteType' | 'metric') => void;
  /** Set points color mode ('voteType' or 'metric') */
  setPointsColor: (color: 'voteType' | 'metric') => void;
  /** Set the color scheme for metric visualization */
  setMetricColorScheme: (scheme: MetricColorScheme) => void;
  /** Set the color for vote support (yea) */
  setVoteSupportColor: (color: string) => void;
  /** Set the color for vote against (nay) */
  setVoteAgainstColor: (color: string) => void;
  /** Set the color for vote absence (no-vote) */
  setVoteAbsenceColor: (color: string) => void;
  /** Set the color for districts with no data */
  setVoteNoDataColor: (color: string) => void;
  /** Toggle whether to use 'Feature' label instead of metric title in tooltips */
  setUseFeatureLabel: (b: boolean) => void;

  // Hover handlers - granular setters
  /** Set the source of hover state */
  setHoverSource: (source: 'map' | 'plot' | null) => void;
  /** Set which districts are currently hovered */
  setHoverDistricts: (ids: string[]) => void;
  /** Set the map tooltip position */
  setMapTooltipPos: (pos: MapTooltipPos | null) => void;

  // Hover handlers - convenience functions
  /** Set hover state from plot interaction */
  setHoverFromPlot: (districtIds: string[]) => void;
  /** Set hover state from map interaction with tooltip position (hover only, not locked) */
  setHoverFromMap: (id: string, pos: MapTooltipPos) => void;
  /**
   * Set selection state from a map click.
   *
   * This locks the tooltip to the given district until cleared.
   */
  setSelectedDistrictFromMap: (id: string, pos: MapTooltipPos) => void;
  /** Set districts as selected from plot click (locks selection for multiple districts) */
  setSelectedDistrictsFromPlot: (districtIds: string[]) => void;
  /** Clear all transient hover state (does not clear a locked selection) */
  clearHover: () => void;
  /** Clear the explicit map selection and associated hover/tooltip state */
  clearSelection: () => void;

  /** Open the data drawer */
  openDataDrawer: () => void;
  /** Close the data drawer */
  closeDataDrawer: () => void;
  /** Toggle the data drawer */
  toggleDataDrawer: () => void;

  // Drawer visibility
  /** Open the map drawer */
  openDrawer: () => void;
  /** Close the map drawer */
  closeDrawer: () => void;
  /** Toggle the map drawer */
  toggleDrawer: () => void;

  // Convenience
  /** Update multiple visualization options at once */
  patch: (partial: Partial<VizOptions>) => void;
  /** Reset all visualization options to defaults */
  resetViz: () => void;
};

/**
 * Viz Store - Manages visualization options and transient UI state.
 *
 * This store handles:
 * - Visualization settings (colors, bin numbers, display options)
 * - Transient UI state (hover states, tooltip positions, drawer visibility)
 * - User preference persistence
 *
 * **Persistence**:
 * - The store uses Zustand's persist middleware to save visualization options
 *   to localStorage (colors, bins, display options)
 * - Transient UI state (hover, tooltip, drawer) is NOT persisted and resets
 *   on page reload
 *
 * @see VizStore - Type definition for this store
 * @see useVizStore - Hook to access this store
 */
const vizStore = create<VizStore>()(
  persist(
    (set) => ({
      ...initialViz,

      // Hover + selection state (not persisted)
      hoveredDistricts: [],
      hoverSource: null,
      selectedDistrictId: null,
      selectedDistricts: [],
      mapTooltipPos: null,

      setBinNum: (binNum) => set({ binNum }),
      setShowPoints: (showPoints) => set({ showPoints }),
      setUseBins: (useBins) => set({ useBins }),

      setPolyColor: (polyColor) => set({ polyColor }),
      setPointsColor: (pointsColor) => set({ pointsColor }),
      setMetricColorScheme: (metricColorScheme) => set({ metricColorScheme }),
      setVoteSupportColor: (voteSupportColor) => set({ voteSupportColor }),
      setVoteAgainstColor: (voteAgainstColor) => set({ voteAgainstColor }),
      setVoteAbsenceColor: (voteAbsenceColor) => set({ voteAbsenceColor }),
      setVoteNoDataColor: (voteNoDataColor) => set({ voteNoDataColor }),
      setUseFeatureLabel: (useFeatureLabel) => set({ useFeatureLabel }),

      setHoverSource: (hoverSource) => set({ hoverSource }),
      setHoverDistricts: (hoveredDistricts) => set({ hoveredDistricts }),
      setMapTooltipPos: (mapTooltipPos) => set({ mapTooltipPos }),

      setHoverFromPlot: (districtIds) =>
        set((state) => {
          // Don't override state if districts are selected (locked)
          if (state.selectedDistrictId || state.selectedDistricts.length > 0) {
            return {};
          }
          return { hoveredDistricts: districtIds, hoverSource: 'plot', mapTooltipPos: null };
        }),
      setHoverFromMap: (id, mapTooltipPos) =>
        set((state) => {
          // Don't override state if districts are selected (locked)
          if (state.selectedDistrictId || state.selectedDistricts.length > 0) {
            return {};
          }
          return { hoveredDistricts: [id], hoverSource: 'map', mapTooltipPos };
        }),
      setSelectedDistrictFromMap: (id, mapTooltipPos) =>
        set({
          selectedDistrictId: id,
          selectedDistricts: [], // Clear multi-selection when using single selection
          hoveredDistricts: [id],
          hoverSource: 'map',
          mapTooltipPos,
        }),
      setSelectedDistrictsFromPlot: (districtIds) =>
        set({
          selectedDistrictId: null, // Clear single selection when using multi-selection
          selectedDistricts: districtIds,
          hoveredDistricts: districtIds,
          hoverSource: 'plot',
          // Keep existing mapTooltipPos if set, otherwise null
          // This allows the map tooltip to persist if it was already shown
        }),
      clearHover: () =>
        set((state) => {
          // Don't clear hover state if districts are selected (locked)
          if (state.selectedDistrictId || state.selectedDistricts.length > 0) {
            return {};
          }
          return { hoveredDistricts: [], hoverSource: null };
        }),
      clearSelection: () =>
        set({
          selectedDistrictId: null,
          selectedDistricts: [],
          hoveredDistricts: [],
          hoverSource: null,
          mapTooltipPos: null,
        }),

      openDataDrawer: () => set({ dataDrawerOpen: true }),
      closeDataDrawer: () => set({ dataDrawerOpen: false }),
      toggleDataDrawer: () => set((s) => ({ dataDrawerOpen: !s.dataDrawerOpen })),

      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),
      toggleDrawer: () => set((s) => ({ drawerOpen: !s.drawerOpen })),

      patch: (partial) => set(partial),
      resetViz: () =>
        set({
          ...initialViz,
          hoveredDistricts: [],
          hoverSource: null,
          selectedDistrictId: null,
          selectedDistricts: [],
          mapTooltipPos: null,
        }),
    }),
    {
      name: 'viz-options',
      partialize: (state) => {
        // Only persist visualization options, exclude UI state and action functions
        return {
          showPoints: state.showPoints,
          binNum: state.binNum,
          useBins: state.useBins,
          polyColor: state.polyColor,
          pointsColor: state.pointsColor,
          metricColorScheme: state.metricColorScheme,
          voteSupportColor: state.voteSupportColor,
          voteAgainstColor: state.voteAgainstColor,
          voteAbsenceColor: state.voteAbsenceColor,
          voteNoDataColor: state.voteNoDataColor,
          useFeatureLabel: state.useFeatureLabel,
          // Note: drawerOpen is intentionally excluded from persistence
        };
      },
    },
  ),
);

/**
 * Hook to access the Viz Store.
 *
 * Use this hook in React components to read and update visualization state.
 * Supports equality functions for optimized re-renders.
 *
 * Visualization options are automatically persisted to localStorage.
 *
 * @example
 * ```tsx
 * const binNum = useVizStore((state) => state.binNum);
 * const setBinNum = useVizStore((state) => state.setBinNum);
 * const hoveredDistricts = useVizStore((state) => state.hoveredDistricts);
 * ```
 */
export const useVizStore = vizStore as unknown as UseStoreWithEquality<VizStore>;
